import { type CacheDatum, CacheDatumState, type LoadedCacheDatum } from "@/cache/CacheDatumState";
import { lru_evict } from "@/cache/evictionStrategy";
import { cacheDatumEstimatedByteSize } from "@/cache/utils/cacheMemory";

type AnyCacheDatum = CacheDatum<unknown, unknown, unknown>;

/**
 * Mark a loaded cache entry as recently used so future LRU passes keep it longer.
 */
export function markCacheDatumUsed<T>(cacheLine: LoadedCacheDatum<T>) {
  cacheLine.lastUsed = performance.now();
}

/**
 * Return the age timestamp used by eviction or `null` if the entry must not be evicted.
 */
function evictableEntryAge(cacheLine: AnyCacheDatum): number | null {
  switch (cacheLine.state) {
    case CacheDatumState.Pending:
      return null;
    case CacheDatumState.Loaded:
      return cacheLine.lastUsed;
    case CacheDatumState.PermanentlyFailed:
      return cacheLine.requestDatetime;
    default: {
      const _exhaustive: never = cacheLine;
      return _exhaustive;
    }
  }
}

/**
 * Count cache entries that are currently eligible for eviction.
 */
export function countEvictableCacheEntries<T, B, C>(cache: Record<string, CacheDatum<T, B, C>>): number {
  let count = 0;

  for (const cacheLine of Object.values(cache)) {
    if (evictableEntryAge(cacheLine as AnyCacheDatum) != null) {
      count++;
    }
  }

  return count;
}

/**
 * Evict cache entries by count using LRU order.
 * This helper exists for older count-bounded caches and regression tests.
 */
export function evictCacheEntriesByCount<T, B, C>(
  cache: Record<string, CacheDatum<T, B, C>>,
  maxEntries: number,
  delayEvictionUntilEntries: number,
  unload: (cacheLine: CacheDatum<T, B, C> | undefined, key: string) => void,
) {
  const evictableEntryCount = countEvictableCacheEntries(cache);
  const excessEntries = evictableEntryCount - maxEntries;

  if (evictableEntryCount <= delayEvictionUntilEntries || excessEntries <= 0) {
    return;
  }

  lru_evict<CacheDatum<T, B, C> | undefined, string>(
    excessEntries,
    cache,
    (cacheLine) => (cacheLine ? evictableEntryAge(cacheLine as AnyCacheDatum) : null),
    () => 1,
    unload,
  );
}

/**
 * Evict cache entries by approximate retained size using LRU order.
 */
export function evictCacheEntriesBySize<T, B, C>(
  cache: Record<string, CacheDatum<T, B, C>>,
  maxSize: number,
  delayEvictionUntilSize: number,
  unload: (cacheLine: CacheDatum<T, B, C> | undefined, key: string) => void,
) {
  let evictableSize = 0;

  for (const cacheLine of Object.values(cache)) {
    evictableSize += cacheDatumEstimatedByteSize(cacheLine);
  }

  const excessSize = evictableSize - maxSize;

  if (evictableSize <= delayEvictionUntilSize || excessSize <= 0) {
    return;
  }

  lru_evict<CacheDatum<T, B, C> | undefined, string>(
    excessSize,
    cache,
    (cacheLine) => (cacheLine ? evictableEntryAge(cacheLine as AnyCacheDatum) : null),
    (cacheLine) => Math.max(1, cacheDatumEstimatedByteSize(cacheLine)),
    unload,
  );
}
